import { useState, useEffect, useCallback } from "react";
import { API } from "../App";
import { Upload, FileSpreadsheet, Send, CheckCircle, Loader2, Trash2, Edit, FileText } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog } from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import Layout from "../components/Layout";
import { StatCard, PaymentCard } from "../components/payments/PaymentCard";
import { PreviewEmailDialog, EditDraftDialog, SendEmailDialog } from "../components/payments/PaymentDialogs";


const IncompletePaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState("all");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Dialogs
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [sendEmailOpen, setSendEmailOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [editedDraft, setEditedDraft] = useState("");
  const [providerEmail, setProviderEmail] = useState("");
  const [sending, setSending] = useState(false);

  const fetchPayments = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (selectedBatch !== "all") params.append("batch_id", selectedBatch);
      
      const response = await axios.get(`${API}/incomplete-payments?${params.toString()}`, {
        withCredentials: true
      });
      
      setPayments(response.data.payments || []);
      setBatches(response.data.batches || []);
    } catch (error) {
      toast.error("Failed to fetch incomplete payments");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, selectedBatch]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = [".csv", ".xlsx", ".xls"];
    const fileExt = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!validTypes.includes(fileExt)) {
      toast.error("Please upload a CSV or Excel file");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/incomplete-payments/upload`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success(`Uploaded successfully! Created ${response.data.drafts_created} draft emails.`);
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const updatePaymentStatus = async (paymentId, newStatus) => {
    try {
      await axios.put(`${API}/incomplete-payments/${paymentId}`, 
        { status: newStatus }, 
        { withCredentials: true }
      );
      setPayments(payments.map(p => p.id === paymentId ? { ...p, status: newStatus } : p));
      toast.success("Status updated");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handlePreview = (payment) => {
    setSelectedPayment(payment);
    setPreviewOpen(true);
  };

  const handleEdit = (payment) => {
    setSelectedPayment(payment);
    setEditedDraft(payment.email_draft);
    setEditOpen(true);
  };

  const handleSaveDraft = async () => {
    if (!selectedPayment) return;
    setSavingDraft(true);
    try {
      await axios.put(`${API}/incomplete-payments/${selectedPayment.id}`, 
        { email_draft: editedDraft }, 
        { withCredentials: true }
      );
      setPayments(payments.map(p => 
        p.id === selectedPayment.id ? { ...p, email_draft: editedDraft } : p
      ));
      toast.success("Draft updated");
      setEditOpen(false);
    } catch (error) {
      toast.error("Failed to save draft");
    } finally {
      setSavingDraft(false);
    }
  };

  const handleSendEmail = async (payment) => {
    // If payment already has email, use it; otherwise open dialog
    if (payment.provider_email) {
      setSending(true);
      try {
        await axios.post(`${API}/incomplete-payments/${payment.id}/send`, 
          { provider_email: payment.provider_email }, 
          { withCredentials: true }
        );
        setPayments(payments.map(p => 
          p.id === payment.id ? { ...p, status: "sent" } : p
        ));
        toast.success("Email sent successfully!");
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to send email");
      } finally {
        setSending(false);
      }
    } else {
      // Open dialog to collect email
      setSelectedPayment(payment);
      setProviderEmail("");
      setSendEmailOpen(true);
    }
  };

  const handleConfirmSendEmail = async () => {
    if (!selectedPayment || !providerEmail) {
      toast.error("Please enter the provider's email address");
      return;
    }
    
    setSending(true);
    try {
      await axios.post(`${API}/incomplete-payments/${selectedPayment.id}/send`, 
        { provider_email: providerEmail }, 
        { withCredentials: true }
      );
      setPayments(payments.map(p => 
        p.id === selectedPayment.id ? { ...p, status: "sent", provider_email: providerEmail } : p
      ));
      toast.success("Email sent successfully via SES!");
      setSendEmailOpen(false);
      setProviderEmail("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const handleDeleteBatch = async (batchId) => {
    if (!confirm("Are you sure you want to delete this batch and all its payments?")) return;
    
    try {
      await axios.delete(`${API}/incomplete-payments/batch/${batchId}`, {
        withCredentials: true
      });
      toast.success("Batch deleted");
      setSelectedBatch("all");
      fetchPayments();
    } catch (error) {
      toast.error("Failed to delete batch");
    }
  };

  // Filter payments by search query
  const filteredPayments = payments.filter(payment => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      payment.provider?.toLowerCase().includes(query) ||
      payment.client?.toLowerCase().includes(query) ||
      payment.provider_invoice_no?.toLowerCase().includes(query) ||
      payment.issue?.toLowerCase().includes(query)
    );
  });

  // Calculate stats
  const stats = {
    total: payments.length,
    draft: payments.filter(p => p.status === "draft").length,
    sent: payments.filter(p => p.status === "sent").length,
    complete: payments.filter(p => p.status === "complete").length
  };

  if (loading) {
    return (
      <Layout title="Incomplete Payments">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#425B76]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Incomplete Payments">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <p className="text-slate-500">
              Upload payment reports and send follow-up emails to providers with payment issues
            </p>
          </div>
          <div className="flex gap-2">
            <input
              type="file"
              id="file-upload"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              onClick={() => document.getElementById("file-upload")?.click()}
              disabled={uploading}
              className="bg-[#425B76] hover:bg-[#2D4156] gap-2"
              data-testid="upload-file-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Upload Report
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            title="Total Payments"
            value={stats.total}
            icon={FileSpreadsheet}
            color="bg-[#425B76]/10 text-[#425B76]"
          />
          <StatCard
            title="Drafts"
            value={stats.draft}
            icon={FileText}
            color="bg-slate-100 text-slate-600"
          />
          <StatCard
            title="Sent"
            value={stats.sent}
            icon={Send}
            color="bg-blue-100 text-blue-600"
          />
          <StatCard
            title="Complete"
            value={stats.complete}
            icon={CheckCircle}
            color="bg-emerald-100 text-emerald-600"
          />
        </div>

        {/* Filters */}
        <Card className="bg-white border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Input
                  placeholder="Search by provider, client, invoice..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-input"
                />
              </div>
              
              {/* Batch Filter */}
              <Select value={selectedBatch} onValueChange={setSelectedBatch}>
                <SelectTrigger className="w-[200px]" data-testid="batch-filter">
                  <SelectValue placeholder="Select Batch" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Batches</SelectItem>
                  {batches.map((batch) => (
                    <SelectItem key={batch.id} value={batch.id}>
                      {new Date(batch.uploaded_at).toLocaleDateString()} ({batch.count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Drafts</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="complete">Complete</SelectItem>
                </SelectContent>
              </Select>

              {selectedBatch !== "all" && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteBatch(selectedBatch)}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Delete Batch
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Payments List */}
        {filteredPayments.length === 0 ? (
          <Card className="bg-white border-slate-200">
            <CardContent className="p-12 text-center">
              <FileSpreadsheet className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">
                {payments.length === 0 ? "No Incomplete Payments" : "No Results Match Filters"}
              </h3>
              <p className="text-slate-500 mb-4">
                {payments.length === 0 
                  ? "Upload a CSV or Excel file to generate draft follow-up emails."
                  : "Try adjusting your search or filters."}
              </p>
              {payments.length === 0 && (
                <Button
                  onClick={() => document.getElementById("file-upload")?.click()}
                  disabled={uploading}
                  className="bg-[#425B76] hover:bg-[#2D4156] gap-2"
                >
                  <Upload className="w-4 h-4" />
                  Upload Report
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-500">
              Showing {filteredPayments.length} of {payments.length} payments
            </p>
            {filteredPayments.map((payment) => (
              <PaymentCard
                key={payment.id}
                payment={payment}
                onStatusChange={updatePaymentStatus}
                onPreview={handlePreview}
                onEdit={handleEdit}
                onSend={handleSendEmail}
              />
            ))}
          </div>
        )}
      </div>

      {/* Preview Dialog */}
      <PreviewEmailDialog open={previewOpen} onOpenChange={setPreviewOpen} payment={selectedPayment} onSend={handleSendEmail} />

      {/* Edit Dialog */}
      <EditDraftDialog open={editOpen} onOpenChange={setEditOpen} payment={selectedPayment} editedDraft={editedDraft} setEditedDraft={setEditedDraft} onSave={handleSaveDraft} saving={savingDraft} />

      {/* Send Email Dialog */}
      <SendEmailDialog open={sendEmailOpen} onOpenChange={setSendEmailOpen} payment={selectedPayment} providerEmail={providerEmail} setProviderEmail={setProviderEmail} onConfirm={handleConfirmSendEmail} sending={sending} />
    </Layout>
  );
};

export default IncompletePaymentsPage;
